import { Component, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../app.state';
import { CalculationModelsState, CalculationState, selectCalculation, selectModels } from '../../../calculation.state';
import { map } from 'rxjs/operators';
import { setCalculateAccessories, setCustomTiles } from '../../../calculation.actions';
import { formatNumber } from '../../../../common/shared/helpers/number';
import { Subscription } from 'rxjs';
import { CustomMeasurementDto } from '../../../../../api/models/custom-measurement-dto';
import { cloneDeep } from 'lodash-es';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CalculationTypeEnum } from '../../../../../api/models/calculation-type-enum';

@Component({
  selector: 'calculation-custom',
  templateUrl: './custom.component.html',
  styleUrls: ['./custom.component.scss'],
})
export class CustomComponent implements OnInit, OnDestroy {
  @Output()
  forms: UntypedFormGroup[] = [];
  formSubscription: any;
  storeSubscription: any;
  measurements!: CustomMeasurementDto;
  private modelsSubscription!: Subscription;
  models?: CalculationModelsState;
  private activeCalculation!: CalculationState;
  constructor(
    private _formBuilder: UntypedFormBuilder,
    private store: Store<AppState>,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.storeSubscription = this.store.pipe(map((state) => selectCalculation(state))).subscribe((calculation) => {
      if (calculation.roofType !== CalculationTypeEnum.Custom) {
        return;
      }
      this.activeCalculation = calculation;

      if (JSON.stringify(this.measurements) !== JSON.stringify(calculation.measurements)) {
        this.measurements = calculation.measurements;
        this.setupForms(calculation.measurements);
      }
    });
    this.modelsSubscription = this.store.pipe(map((state) => selectModels(state))).subscribe((models) => {
      this.models = models as CalculationModelsState;
    });
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
    this.modelsSubscription?.unsubscribe();
  }

  private setupForms(measurements: CustomMeasurementDto) {
    this.forms = [];
    measurements.tilesByHeight?.forEach((tile, index) => {
      const config: any = {};
      config.height = new UntypedFormControl(tile.height, [Validators.required, Validators.pattern(/^\d+[,.]?\d*$/)]);
      config.count = new UntypedFormControl(tile.count, [Validators.min(1), Validators.pattern(/^\d+$/)]);
      this.forms[index] = this._formBuilder.group(config);
    });
  }

  private roundToCutHeight(height: number) {
    const rest = (height * 100) % 35;
    if (rest < 15 && rest > 5) {
      height += (15 - rest) / 100;
    }

    return height;
  }

  formDecimalInputChange(event: Event, index: number, formControlName: string) {
    let value = (event.target as HTMLInputElement).value.replace(',', '.').replace(/\s/g, '');
    if (value !== '' && Number(value) <= 0.5) {
      value = '0.5';
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore the error is retarded since I'm not doing a strict check
    const valueAsNumber: number = Number(value) == value ? formatNumber(Number(value), 2) : Number(value);
    if (isNaN(valueAsNumber)) {
      this.forms[index].controls[formControlName].patchValue(null);
    } else if (valueAsNumber > 7) {
      this.forms[index].controls[formControlName].patchValue(7);
    } else {
      this.forms[index].controls[formControlName].patchValue(formatNumber(this.roundToCutHeight(valueAsNumber)));
    }
    if (valueAsNumber !== this.roundToCutHeight(valueAsNumber)) {
      this.snackBar.open('Lungimea a fost rotunjita dupa cerintele de debitare a producatorului', 'OK', {
        duration: 10000,
        verticalPosition: 'top',
      });
    }
    this.forms[index].markAllAsTouched();
  }

  formIntegerInputChange(event: Event, index: number, formControlName: string) {
    let value = (event.target as HTMLInputElement).value;
    if (value !== '' && Number(value) <= 1) {
      value = '1';
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore the error is retarded since I'm not doing a strict check
    let valueAsNumber: number | null = Number(value);
    if (isNaN(valueAsNumber)) {
      valueAsNumber = null;
    }
    this.forms[index].controls[formControlName].patchValue(valueAsNumber);
    this.forms[index].markAllAsTouched();
  }

  get allFormsAreValid(): boolean {
    if (this.forms.length <= 1) {
      return true;
    }
    return this.forms.every((form) => form.valid);
  }

  addHeight() {
    const clone = cloneDeep(this.measurements);
    clone.tilesByHeight?.push({ count: null, height: null } as any);
    this.measurements = clone;
    const config: any = {};
    config.height = new UntypedFormControl(null, [Validators.required, Validators.pattern(/^\d+[,.]?\d*$/)]);
    config.count = new UntypedFormControl(null, [Validators.min(1), Validators.pattern(/^\d+$/)]);
    this.forms.push(this._formBuilder.group(config));
  }

  remove(index: number) {
    const clone = cloneDeep(this.measurements);
    clone.tilesByHeight?.splice(index, 1);
    this.measurements = clone;
    this.forms.splice(index, 1);
  }

  get nextButtonLabel() {
    if (this.forms.length === 1) {
      return !this.forms[0].valid ? 'Nu doresc tabla' : 'Accesorii';
    } else if (this.forms.length === 0) {
      return 'Nu doresc tabla';
    }
    return 'Accesorii';
  }

  updateState() {
    this.store.dispatch(
      setCustomTiles({
        tiles: this.forms.map((form) => ({
          height: Number(form.controls.height.value),
          count: Number(form.controls.count.value),
        })),
      }),
    );
    if (this.forms.length > 0 && this.forms[0].valid && !this.activeCalculation.isLoaded) {
      if (
        this.activeCalculation.accessories?.length &&
        !confirm('Aveti accesorii salvate pe calcul, doriti sa le recalculam ?')
      ) {
        return;
      }
      this.store.dispatch(setCalculateAccessories({ calculateAccessories: true }));
    }
  }
}
